<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="560px"
    @close="handleClose"
  >
    <el-form
      :model="ruleForm"
      :rules="rules"
      ref="ruleForm"
      label-width="100px"
    >
      <el-form-item label="设备型号" prop="devicesModel">
        <ul v-if="!isDefaultForm" class="model-input-list">
          <li
            class="model-input-item"
            v-for="(item, index) in ruleForm.devicesModel"
            :key="index"
          >
            <el-input
              type="text"
              v-model.trim="item.model"
              placeholder="输入型号，如 P13、Palma2"
            />
            <div class="model-input-item-btn">
              <i
                class="el-icon-plus"
                v-if="index === ruleForm.devicesModel.length - 1"
                @click="addModelRow"
                title="添加项"
              ></i>
              <i
                class="el-icon-minus"
                v-if="index > 0"
                @click="deleteModelRow(index)"
                title="删除项"
              ></i>
            </div>
          </li>
        </ul>
        <el-input v-else value="默认（未单独配置的型号）" disabled />
      </el-form-item>
      <el-form-item label="启用状态">
        <el-switch v-model="ruleForm.enabled" :disabled="isDefaultForm" />
      </el-form-item>
      <el-form-item label="支持地区" prop="allowedRegions">
        <el-select
          v-model="ruleForm.allowedRegions"
          multiple
          filterable
          :filter-method="filterRegion"
          @change="normalizeRegions"
          placeholder="请选择国家/地区，如 韩国 / KR"
          style="width: 100%"
          :disabled="isDefaultForm"
        >
          <el-option
            v-for="region in filteredRegions"
            :key="region.code"
            :label="`${region.name}（${region.code}）`"
            :value="region.code"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">{{
        isDefaultForm ? "关闭" : "取消"
      }}</el-button>
      <el-button v-if="!isDefaultForm" type="primary" @click="handleConfirm"
        >确定</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
const REGION_PATTERN = /^[A-Z]{2}$/;

export default {
  name: "DeviceConfigDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: String,
      default: null,
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
    regionOptions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    isDefaultForm() {
      return this.ruleForm.isDefault;
    },
    dialogTitle() {
      if (this.isDefaultForm) return "查看默认配置";
      return this.editId ? "编辑设备配置" : "新增设备配置";
    },
  },
  data() {
    const validateRegions = (rule, value, callback) => {
      const invalid = (value || []).filter((r) => !REGION_PATTERN.test(r));
      if (invalid.length > 0) {
        callback(
          new Error(
            `地区码格式不正确：${invalid.join("、")}，须为两位大写国家码`
          )
        );
      } else {
        callback();
      }
    };

    return {
      ruleForm: {
        devicesModel: [{ model: "" }],
        enabled: true,
        allowedRegions: [],
        isDefault: false,
      },
      rules: {
        devicesModel: [
          {
            type: "array",
            required: true,
            message: "请至少输入一个设备型号",
            trigger: "change",
          },
        ],
        allowedRegions: [{ validator: validateRegions, trigger: "change" }],
      },
      filteredRegions: this.regionOptions,
    };
  },
  methods: {
    fillForm(data = {}) {
      const models = (data.models || []).filter(
        (m) => m && m !== "__default__"
      );
      this.ruleForm = {
        devicesModel: models.length
          ? models.map((m) => ({ model: m }))
          : [{ model: "" }],
        enabled:
          data.enabledRaw !== undefined
            ? data.enabledRaw
            : data.enabled !== false,
        allowedRegions: data.allowedRegions || [],
        isDefault: !!data.isDefault,
      };
    },

    resetForm() {
      this.ruleForm = {
        devicesModel: [{ model: "" }],
        enabled: true,
        allowedRegions: [],
        isDefault: false,
      };
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.clearValidate();
      }
    },

    handleClose() {
      this.resetForm();
      this.$emit("close");
    },

    addModelRow() {
      this.ruleForm.devicesModel = [
        ...this.ruleForm.devicesModel,
        { model: "" },
      ];
    },

    deleteModelRow(index) {
      this.ruleForm.devicesModel.splice(index, 1);
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.validateField("devicesModel").catch(() => {});
      }
    },

    filterRegion(query) {
      const keyword = (query || "").trim().toLowerCase();
      if (!keyword) {
        this.filteredRegions = this.regionOptions;
        return;
      }
      this.filteredRegions = this.regionOptions.filter(
        (item) =>
          item.code.toLowerCase().includes(keyword) ||
          (item.name && item.name.toLowerCase().includes(keyword))
      );
    },

    normalizeRegions(values) {
      const nameToCode = Object.fromEntries(
        this.regionOptions.map((item) => [item.name, item.code])
      );
      this.ruleForm.allowedRegions = Array.from(
        new Set(
          (values || []).map((value) => {
            const text = String(value).trim();
            if (REGION_PATTERN.test(text.toUpperCase()))
              return text.toUpperCase();
            return nameToCode[text] || text.toUpperCase();
          })
        )
      );
      this.filteredRegions = this.regionOptions;
    },

    handleConfirm() {
      this.$refs.ruleForm.validate((valid) => {
        if (!valid) return;
        const models = Array.from(
          new Set(
            this.ruleForm.devicesModel
              .map((item) => (item.model || "").trim())
              .filter(Boolean)
          )
        );
        if (!models.length) {
          this.$message({ type: "warning", message: "请至少输入一个设备型号" });
          return;
        }
        this.$emit("confirm", {
          models,
          enabled: this.ruleForm.enabled,
          allowedRegions: this.ruleForm.allowedRegions,
        });
      });
    },
  },
  watch: {
    regionOptions: {
      immediate: true,
      handler(val) {
        this.filteredRegions = val || [];
      },
    },
    visible(val) {
      if (val && this.formData) {
        this.fillForm(this.formData);
      }
      if (!val) {
        this.resetForm();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.model-input-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.model-input-item {
  position: relative;
  margin-bottom: 14px;
  padding-right: 56px;

  .model-input-item-btn {
    position: absolute;
    top: 50%;
    right: 8px;
    width: 40px;
    display: flex;
    justify-content: space-between;
    transform: translate(0, -50%);

    > i {
      cursor: pointer;
      font-size: 16px;
      color: #606266;

      &:hover {
        color: #409eff;
      }
    }
  }
}
</style>
